import { useEffect, useMemo, useState, useCallback } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { useInterviews, type Interview } from '@/hooks/useInterviews'
import { useTalentBank, type TalentBankEntry } from '@/hooks/useTalentBank'
import { useExpressCandidates } from '@/hooks/useExpressCandidates'
import { lumi } from '@/lib/lumi'
import {
  RESULT_LABELS,
  RESULT_STYLES,
  INTERVIEW_PROC_LABELS,
  INTERVIEW_PROC_STYLES,
} from '@/lib/recruitment'
import {Users, ClipboardList, Star, Search, SlidersHorizontal, ChevronDown, X, TrendingUp, Building2, UserPlus, Phone, Download, } from 'lucide-react'

type Tab = 'entrevistas' | 'banco' | 'express'

/* ---- utilidades Excel (CSV con BOM) ---- */
function calcAge(birthDate?: string): string {
  if (!birthDate) return ''
  const dob = new Date(birthDate)
  if (isNaN(dob.getTime())) return ''
  const diff = Date.now() - dob.getTime()
  return String(Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25)))
}

interface ExportRow { nombre: string; telefono: string; correo: string; edad: string }

function exportToExcel(rows: ExportRow[], filename: string) {
  const header = ['Nombre', 'Teléfono', 'Correo', 'Edad']
  const escape = (v: string) => `"${(v || '').replace(/"/g, '""')}"`
  const lines = [
    header.map(escape).join(','),
    ...rows.map((r) => [escape(r.nombre), escape(r.telefono), escape(r.correo), escape(r.edad)].join(',')),
  ]
  const bom = '\uFEFF'
  const blob = new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/* ---- Tipos para el modal de detalles ---- */
interface CandidateProfile {
  name: string
  email: string
  phone?: string
  birthDate?: string
  photo?: string
  headline?: string
  city?: string
  state?: string
  skills?: string[]
  languages?: string[]
  certifications?: string[]
  cvUrl?: string
  vacancyTitle?: string
  lastScore?: number
  notes?: string
  tags?: string[]
  classification?: string
  source?: string // 'interview' | 'banco' | 'express'
  companyName?: string
}

function CandidateDetailModal({ profile, onClose }: { profile: CandidateProfile; onClose: () => void }) {
  const age = calcAge(profile.birthDate)
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-2xl bg-slate-900 px-6 py-4">
          <h2 className="text-base font-semibold text-white">Perfil del candidato</h2>
          <button onClick={onClose} className="text-slate-300 hover:text-white">
            <X size={18} />
          </button>
        </div>
        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Foto + nombre */}
          <div className="flex items-center gap-4">
            {profile.photo ? (
              <img src={profile.photo} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-2xl font-bold text-amber-700">
                {(profile.name || 'C')[0].toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-lg font-bold text-slate-900">{profile.name || '—'}</p>
              {profile.headline && <p className="text-sm text-slate-500">{profile.headline}</p>}
              {profile.companyName && (
                <p className="text-xs text-slate-400 inline-flex items-center gap-1 mt-0.5">
                  <Building2 size={11} /> {profile.companyName}
                </p>
              )}
            </div>
          </div>

          {/* Datos de contacto */}
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4">
            <div>
              <p className="text-[10px] font-semibold uppercase text-slate-400">Correo</p>
              <p className="text-sm text-slate-800 break-all">{profile.email || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-slate-400">Teléfono</p>
              <p className="text-sm text-slate-800">{profile.phone || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-slate-400">Edad</p>
              <p className="text-sm text-slate-800">{age ? `${age} años` : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-slate-400">Ubicación</p>
              <p className="text-sm text-slate-800">
                {[profile.city, profile.state].filter(Boolean).join(', ') || '—'}
              </p>
            </div>
          </div>

          {/* Vacante / calificación */}
          {(profile.vacancyTitle || typeof profile.lastScore === 'number') && (
            <div className="grid grid-cols-2 gap-3">
              {profile.vacancyTitle && (
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-400">Última vacante</p>
                  <p className="text-sm text-slate-800">{profile.vacancyTitle}</p>
                </div>
              )}
              {typeof profile.lastScore === 'number' && (
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-400">Calificación</p>
                  <p className="text-sm inline-flex items-center gap-1 font-semibold text-amber-700">
                    <Star size={13} className="text-amber-400" /> {profile.lastScore}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Clasificación / etiquetas */}
          {(profile.classification || (profile.tags || []).length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {profile.classification && (
                <span className="rounded-full bg-violet-50 px-3 py-0.5 text-xs font-semibold text-violet-700">
                  {profile.classification}
                </span>
              )}
              {(profile.tags || []).map((t) => (
                <span key={t} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">#{t}</span>
              ))}
            </div>
          )}

          {/* Habilidades */}
          {(profile.skills || []).length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase text-slate-400">Habilidades</p>
              <div className="flex flex-wrap gap-1.5">
                {(profile.skills || []).map((s) => (
                  <span key={s} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Idiomas */}
          {(profile.languages || []).length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase text-slate-400">Idiomas</p>
              <div className="flex flex-wrap gap-1.5">
                {(profile.languages || []).map((l) => (
                  <span key={l} className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">{l}</span>
                ))}
              </div>
            </div>
          )}

          {/* Certificaciones */}
          {(profile.certifications || []).length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase text-slate-400">Certificaciones</p>
              <div className="flex flex-wrap gap-1.5">
                {(profile.certifications || []).map((c) => (
                  <span key={c} className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-700">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notas */}
          {profile.notes && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Notas</p>
              <p className="text-sm text-slate-700">{profile.notes}</p>
            </div>
          )}

          {/* CV */}
          {profile.cvUrl && (
            <a
              href={profile.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download size={14} /> Descargar CV
            </a>
          )}
        </div>

        {/* Footer export */}
        <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            onClick={() =>
              exportToExcel(
                [{ nombre: profile.name, telefono: profile.phone || '', correo: profile.email, edad: age }],
                `candidato_${profile.name.replace(/\s+/g, '_')}`
              )
            }
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <Download size={14} /> Exportar Excel
          </button>
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---- Componente principal ---- */
function AdminGestionTalento() {
  const { interviews, loading: loadingI, fetchAll: fetchAllI } = useInterviews()
  const { entries, loading: loadingB, fetchAll: fetchAllB } = useTalentBank()
  const { candidates: expressCandidates, loading: loadingE, fetchAll: fetchAllE } = useExpressCandidates()

  const [tab, setTab] = useState<Tab>('entrevistas')
  const [search, setSearch] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const [filterResult, setFilterResult] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Modal de detalles del candidato
  const [detailProfile, setDetailProfile] = useState<CandidateProfile | null>(null)

  // Mapa colaboradorUserId → datos de collaborators
  const [collabMap, setCollabMap] = useState<Record<string, any>>({})

  useEffect(() => {
    fetchAllI()
    fetchAllB()
    fetchAllE()
  }, [fetchAllI, fetchAllB, fetchAllE])

  // Cargar perfiles de colaboradores para enriquecer los detalles
  useEffect(() => {
    const ids = new Set<string>()
    interviews.forEach((i) => i.collaboratorUserId && ids.add(i.collaboratorUserId))
    entries.forEach((e) => e.collaboratorUserId && ids.add(e.collaboratorUserId))
    if (ids.size === 0) return
    lumi.entities.collaborators
      .list({ limit: 200 })
      .then((r) => {
        const map: Record<string, any> = {}
        ;(r.list || []).forEach((c: any) => {
          if (c.userId) map[c.userId] = c
        })
        setCollabMap(map)
      })
      .catch(() => {})
  }, [interviews, entries])

  /* ---- Abrir modal de detalles ---- */
  const openDetail = useCallback(
    (source: 'interview' | 'banco' | 'express', item: any) => {
      let collab: any = {}
      if (source !== 'express') {
        collab = collabMap[item.collaboratorUserId] || {}
      }
      const profile: CandidateProfile = {
        source,
        name:
          source === 'express'
            ? item.fullName || '—'
            : collab.fullName || item.collaboratorName || '—',
        email:
          source === 'express'
            ? item.email || ''
            : collab.email || item.collaboratorEmail || '',
        phone:
          source === 'express'
            ? item.phone || ''
            : collab.phone || '',
        birthDate: collab.birthDate || '',
        photo:
          source === 'banco'
            ? item.collaboratorPhoto || collab.photoUrl || ''
            : collab.photoUrl || item.candidatePhoto || '',
        headline: collab.headline || '',
        city: collab.city || '',
        state: collab.state || '',
        skills: collab.skills || [],
        languages: collab.languages || [],
        certifications: collab.certifications || [],
        cvUrl: collab.cvUrl || '',
        vacancyTitle: item.vacancyTitle || item.lastVacancyTitle || '',
        lastScore:
          typeof item.lastScore === 'number'
            ? item.lastScore
            : typeof item.overallScore === 'number'
            ? item.overallScore
            : undefined,
        notes: item.notes || item.privateNotes || '',
        tags: item.tags || [],
        classification: item.classification || '',
        companyName: item.companyName || '',
      }
      setDetailProfile(profile)
    },
    [collabMap]
  )

  /* ---- Exportar todo el tab actual ---- */
  const exportCurrentTab = useCallback(() => {
    let rows: ExportRow[] = []
    if (tab === 'entrevistas') {
      rows = filteredInterviews.map((i) => {
        const c = collabMap[i.collaboratorUserId] || {}
        return {
          nombre: c.fullName || i.collaboratorName || '',
          telefono: c.phone || '',
          correo: c.email || i.collaboratorEmail || '',
          edad: calcAge(c.birthDate),
        }
      })
      exportToExcel(rows, 'entrevistas_beeznz')
    } else if (tab === 'banco') {
      rows = filteredBank.map((e) => {
        const c = collabMap[e.collaboratorUserId] || {}
        return {
          nombre: c.fullName || e.collaboratorName || '',
          telefono: c.phone || '',
          correo: c.email || e.collaboratorEmail || '',
          edad: calcAge(c.birthDate),
        }
      })
      exportToExcel(rows, 'banco_talento_beeznz')
    } else {
      rows = filteredExpress.map((c) => ({
        nombre: c.fullName || '',
        telefono: c.phone || '',
        correo: c.email || '',
        edad: '',
      }))
      exportToExcel(rows, 'candidatos_express_beeznz')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, collabMap])

  // Empresas únicas para filtro
  const companies = useMemo(() => {
    const set = new Set<string>()
    interviews.forEach((i) => i.companyName && set.add(i.companyName))
    entries.forEach((e) => e.companyName && set.add(e.companyName))
    expressCandidates.forEach((c) => c.companyName && set.add(c.companyName))
    return Array.from(set).sort()
  }, [interviews, entries, expressCandidates])

  // Filtrado de candidatos express
  const filteredExpress = useMemo(() => {
    const q = search.toLowerCase()
    return expressCandidates.filter((c) => {
      const matchSearch =
        !q ||
        (c.fullName || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.companyName || '').toLowerCase().includes(q)
      const matchCompany = !filterCompany || c.companyName === filterCompany
      return matchSearch && matchCompany
    })
  }, [expressCandidates, search, filterCompany])

  const classifications = useMemo(() => {
    const set = new Set<string>()
    entries.forEach((e) => e.classification && set.add(e.classification))
    return Array.from(set).sort()
  }, [entries])

  // KPIs
  const kpis = useMemo(() => {
    const totalInterviews = interviews.length
    const completed = interviews.filter((i) => i.status === 'completada').length
    const approved = interviews.filter((i) => i.result === 'aprobado' || i.result === 'aprobado_observaciones').length
    const inBank = entries.length
    const avgScore =
      interviews.filter((i) => i.overallScore).length > 0
        ? (
            interviews.reduce((acc, i) => acc + (i.overallScore || 0), 0) /
            interviews.filter((i) => i.overallScore).length
          ).toFixed(1)
        : '—'
    const companiesActive = new Set(interviews.map((i) => i.companyUserId)).size
    const expressTotal = expressCandidates.length
    return { totalInterviews, completed, approved, inBank, avgScore, companiesActive, expressTotal }
  }, [interviews, entries, expressCandidates])

  // Filtrado de entrevistas
  const filteredInterviews = useMemo(() => {
    const q = search.toLowerCase()
    return interviews.filter((i) => {
      const matchSearch =
        !q ||
        (i.collaboratorName || '').toLowerCase().includes(q) ||
        (i.collaboratorEmail || '').toLowerCase().includes(q) ||
        (i.vacancyTitle || '').toLowerCase().includes(q) ||
        (i.companyName || '').toLowerCase().includes(q)
      const matchCompany = !filterCompany || i.companyName === filterCompany
      const matchResult = !filterResult || i.result === filterResult
      const matchStatus = !filterStatus || i.status === filterStatus
      return matchSearch && matchCompany && matchResult && matchStatus
    })
  }, [interviews, search, filterCompany, filterResult, filterStatus])

  // Filtrado del banco
  const filteredBank = useMemo(() => {
    const q = search.toLowerCase()
    return entries.filter((e) => {
      const matchSearch =
        !q ||
        (e.collaboratorName || '').toLowerCase().includes(q) ||
        (e.collaboratorEmail || '').toLowerCase().includes(q) ||
        (e.lastVacancyTitle || '').toLowerCase().includes(q) ||
        (e.companyName || '').toLowerCase().includes(q) ||
        (e.tags || []).some((t) => t.toLowerCase().includes(q))
      const matchCompany = !filterCompany || e.companyName === filterCompany
      const matchClass = !filterClass || e.classification === filterClass
      return matchSearch && matchCompany && matchClass
    })
  }, [entries, search, filterCompany, filterClass])

  const clearFilters = () => {
    setSearch('')
    setFilterCompany('')
    setFilterResult('')
    setFilterStatus('')
    setFilterClass('')
  }
  const hasFilters = search || filterCompany || filterResult || filterStatus || filterClass

  const currentCount =
    tab === 'entrevistas'
      ? filteredInterviews.length
      : tab === 'banco'
      ? filteredBank.length
      : filteredExpress.length

  return (
    <AdminLayout title="Gestión de talento" subtitle="Consulta global de entrevistas y banco de talento en toda la plataforma">
      {/* Modal de detalles */}
      {detailProfile && (
        <CandidateDetailModal profile={detailProfile} onClose={() => setDetailProfile(null)} />
      )}

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: 'Entrevistas totales', value: kpis.totalInterviews, color: 'text-slate-800' },
          { label: 'Completadas', value: kpis.completed, color: 'text-emerald-700' },
          { label: 'Aprobados', value: kpis.approved, color: 'text-blue-700' },
          { label: 'En banco de talento', value: kpis.inBank, color: 'text-violet-700' },
          { label: 'Calificación promedio', value: kpis.avgScore, color: 'text-amber-600' },
          { label: 'Candidatos express', value: kpis.expressTotal, color: 'text-rose-600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-gray-500">{label}</p>
            <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mb-5 flex gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm w-fit">
        {([
          { key: 'entrevistas', label: 'Entrevistas', icon: ClipboardList },
          { key: 'banco', label: 'Banco de talento', icon: Users },
          { key: 'express', label: 'Candidatos express', icon: UserPlus },
        ] as { key: Tab; label: string; icon: any }[]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setTab(key); clearFilters() }}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              tab === key
                ? 'bg-slate-900 text-white'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* Filtros + Exportar */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'entrevistas' ? 'Buscar candidato, empresa, vacante...' : 'Buscar candidato, empresa, etiqueta...'}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-amber-400 focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SlidersHorizontal size={15} className="text-gray-400" />
          {companies.length > 0 && (
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            >
              <option value="">Todas las empresas</option>
              {companies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {tab === 'entrevistas' && (
            <>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
              >
                <option value="">Todos los estados</option>
                {(['programada', 'en_proceso', 'completada', 'cancelada'] as const).map((s) => (
                  <option key={s} value={s}>{INTERVIEW_PROC_LABELS[s]}</option>
                ))}
              </select>
              <select
                value={filterResult}
                onChange={(e) => setFilterResult(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
              >
                <option value="">Todos los resultados</option>
                {(Object.keys(RESULT_LABELS) as (keyof typeof RESULT_LABELS)[]).map((r) => (
                  <option key={r} value={r}>{RESULT_LABELS[r]}</option>
                ))}
              </select>
            </>
          )}
          {tab === 'banco' && classifications.length > 0 && (
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            >
              <option value="">Todas las clasificaciones</option>
              {classifications.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
            >
              <X size={13} /> Limpiar
            </button>
          )}
          {/* Botón exportar tab completo */}
          {currentCount > 0 && (
            <button
              onClick={exportCurrentTab}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <Download size={13} /> Exportar Excel ({currentCount})
            </button>
          )}
        </div>
      </div>

      {/* Tabla de entrevistas */}
      {tab === 'entrevistas' && (
        <>
          {loadingI ? (
            <div className="py-16 text-center text-gray-400">Cargando entrevistas...</div>
          ) : filteredInterviews.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
              <ClipboardList className="mx-auto mb-3 text-gray-300" size={36} />
              <p className="text-sm text-gray-500">
                {interviews.length === 0 ? 'No hay entrevistas registradas en la plataforma.' : 'Sin resultados para los filtros aplicados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-3">Candidato</th>
                    <th className="px-4 py-3">Empresa</th>
                    <th className="px-4 py-3">Vacante</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Calificación</th>
                    <th className="px-4 py-3">Resultado</th>
                    <th className="px-4 py-3 text-center">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInterviews.map((it) => (
                    <>
                      <tr
                        key={it._id}
                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                      >
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openDetail('interview', it)}
                            className="flex items-center gap-2 text-left group"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                              {(it.collaboratorName || 'C')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-amber-700 group-hover:underline">
                                {it.collaboratorName || '—'}
                              </p>
                              <p className="text-xs text-gray-400">{it.collaboratorEmail || ''}</p>
                            </div>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Building2 size={13} className="text-gray-400" />
                            {it.companyName || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{it.vacancyTitle || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${INTERVIEW_PROC_STYLES[it.status] || 'bg-gray-100 text-gray-600'}`}>
                            {INTERVIEW_PROC_LABELS[it.status] || it.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {it.overallScore ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-gray-800">
                              <Star size={12} className="text-amber-400" />
                              {it.overallScore}/{it.scaleType || '10'}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {it.result ? (
                            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${RESULT_STYLES[it.result]}`}>
                              {RESULT_LABELS[it.result]}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpandedId(expandedId === it._id ? null : it._id)}
                            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
                          >
                            <ChevronDown
                              size={14}
                              className={`transition-transform ${expandedId === it._id ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </td>
                      </tr>
                      {expandedId === it._id && (
                        <tr key={`exp-${it._id}`} className="bg-slate-50">
                          <td colSpan={7} className="px-6 py-4">
                            <InterviewDetailPanel interview={it} />
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Tabla banco de talento */}
      {tab === 'banco' && (
        <>
          {loadingB ? (
            <div className="py-16 text-center text-gray-400">Cargando banco de talento...</div>
          ) : filteredBank.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
              <Users className="mx-auto mb-3 text-gray-300" size={36} />
              <p className="text-sm text-gray-500">
                {entries.length === 0 ? 'No hay candidatos en el banco de talento.' : 'Sin resultados para los filtros aplicados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-3">Candidato</th>
                    <th className="px-4 py-3">Empresa</th>
                    <th className="px-4 py-3">Última vacante</th>
                    <th className="px-4 py-3">Clasificación</th>
                    <th className="px-4 py-3">Calificación</th>
                    <th className="px-4 py-3">Etiquetas</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBank.map((e) => (
                    <tr key={e._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openDetail('banco', e)}
                          className="flex items-center gap-2.5 text-left group"
                        >
                          {e.collaboratorPhoto ? (
                            <img src={e.collaboratorPhoto} alt="" className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                              {(e.collaboratorName || 'C')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-amber-700 group-hover:underline">{e.collaboratorName || '—'}</p>
                            <p className="text-xs text-gray-400">{e.collaboratorEmail || ''}</p>
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Building2 size={13} className="text-gray-400" />
                          {e.companyName || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{e.lastVacancyTitle || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700">
                          {e.classification || 'Por clasificar'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {typeof e.lastScore === 'number' ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                            <Star size={12} className="text-amber-400" /> {e.lastScore}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(e.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">#{t}</span>
                          ))}
                          {(e.tags || []).length > 3 && (
                            <span className="text-[11px] text-gray-400">+{(e.tags || []).length - 3}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Tabla de candidatos express */}
      {tab === 'express' && (
        <>
          {loadingE ? (
            <div className="py-16 text-center text-gray-400">Cargando candidatos express...</div>
          ) : filteredExpress.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
              <UserPlus className="mx-auto mb-3 text-gray-300" size={36} />
              <p className="text-sm text-gray-500">
                {expressCandidates.length === 0
                  ? 'Ninguna empresa ha registrado candidatos express todavía.'
                  : 'Sin resultados para los filtros aplicados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-3">Candidato</th>
                    <th className="px-4 py-3">Teléfono</th>
                    <th className="px-4 py-3">Empresa</th>
                    <th className="px-4 py-3">Notas</th>
                    <th className="px-4 py-3">Registrado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpress.map((c) => (
                    <tr key={c._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openDetail('express', c)}
                          className="flex items-center gap-2.5 text-left group"
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-xs font-bold text-rose-600">
                            {(c.fullName || 'C')[0].toUpperCase()}
                          </div>
                          <p className="font-semibold text-amber-700 group-hover:underline">{c.fullName || '—'}</p>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Phone size={13} className="text-gray-400" />
                          {c.phone || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Building2 size={13} className="text-gray-400" />
                          {c.companyName || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.notes || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {c.createdAt
                          ? new Date(c.createdAt).toLocaleDateString('es-MX', { dateStyle: 'medium' })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  )
}

/* ---------- Panel de detalle de entrevista ---------- */
function InterviewDetailPanel({ interview: it }: { interview: Interview }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase text-gray-400">Entrevistador</p>
        <p className="text-sm text-gray-800">{it.interviewer || '—'}</p>
        {it.scheduledDate && (
          <>
            <p className="mb-1.5 mt-3 text-xs font-semibold uppercase text-gray-400">Fecha</p>
            <p className="text-sm text-gray-800">
              {new Date(it.scheduledDate).toLocaleString('es-MX', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          </>
        )}
        {it.templateName && (
          <>
            <p className="mb-1.5 mt-3 text-xs font-semibold uppercase text-gray-400">Plantilla utilizada</p>
            <p className="text-sm text-gray-800">{it.templateName}</p>
          </>
        )}
        {it.stage && (
          <>
            <p className="mb-1.5 mt-3 text-xs font-semibold uppercase text-gray-400">Etapa</p>
            <p className="text-sm text-gray-800">{it.stage}</p>
          </>
        )}
      </div>

      {(it.observations || it.strengths || it.opportunities || it.recommendations) && (
        <div>
          {it.observations && (
            <>
              <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Observaciones</p>
              <p className="mb-3 text-sm text-gray-700">{it.observations}</p>
            </>
          )}
          {it.strengths && (
            <>
              <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Fortalezas</p>
              <p className="mb-3 text-sm text-gray-700">{it.strengths}</p>
            </>
          )}
          {it.opportunities && (
            <>
              <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Áreas de oportunidad</p>
              <p className="mb-3 text-sm text-gray-700">{it.opportunities}</p>
            </>
          )}
          {it.recommendations && (
            <>
              <p className="mb-1 text-xs font-semibold uppercase text-gray-400">Recomendaciones</p>
              <p className="text-sm text-gray-700">{it.recommendations}</p>
            </>
          )}
        </div>
      )}

      {(it.competencyScores || []).length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-gray-400">Competencias evaluadas</p>
          <div className="space-y-1.5">
            {(it.competencyScores || []).map((cs) => (
              <div key={cs.name} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{cs.name}</span>
                <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                  <TrendingUp size={12} /> {cs.score}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminGestionTalento
